import { Button, EmptyState, MenuFooter, MenuHeader, Spinner } from "@design";
import { useQuery } from "@tanstack/react-query";
import { BellOff } from "lucide-react";
import { type CSSProperties, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import type { NotificationResponse } from "@/api";
import { Text } from "@/components/Text";
import { NotificationItem } from "./NotificationItem";
import { panelListOptions, unreadCountOptions, useNotificationActions } from "./notificationCache";
import { notificationHref } from "./notificationHref";

const GAP = 8;

/** Mobile (no anchor): a sheet under the top bar. */
const SHEET_STYLE: CSSProperties = { position: "fixed", left: "0.5rem", right: "0.5rem" };

/**
 * Desktop: beside the sidebar, bottom edge level with the bell's, growing upward (the bell
 * sits at the bottom of the sidebar), so no height measuring is needed.
 */
function useBesideAnchor(anchor: HTMLElement | null): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>(SHEET_STYLE);
  useLayoutEffect(() => {
    const place = () => {
      if (!anchor) {
        // Under the mobile top bar, wherever banners above it put it.
        const top = (document.querySelector("header")?.getBoundingClientRect().bottom ?? 0) + GAP;
        setStyle({ ...SHEET_STYLE, top, bottom: "auto", maxHeight: `calc(100dvh - ${top + GAP}px)` });
        return;
      }
      const rect = anchor.getBoundingClientRect();
      // Beside the whole sidebar, not just the bell (which sits at its left edge).
      const sidebar = anchor.closest("nav")?.getBoundingClientRect() ?? rect;
      const viewport = document.documentElement.clientHeight;
      setStyle({
        position: "fixed",
        left: sidebar.right + GAP,
        right: "auto",
        bottom: Math.max(GAP, viewport - rect.bottom),
        maxHeight: Math.max(240, rect.bottom - GAP),
      });
    };
    place();
    globalThis.addEventListener("resize", place);
    return () => globalThis.removeEventListener("resize", place);
  }, [anchor]);
  return style;
}

function PanelBody({ onSelect }: Readonly<{ onSelect: (n: NotificationResponse) => void }>) {
  const { t } = useTranslation("notifications");
  const list = useQuery(panelListOptions());
  if (list.isPending) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner aria-label={t("title")} />
      </div>
    );
  }
  if (list.isError) {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
        <Text variant="body-sm" tone="secondary">
          {t("error")}
        </Text>
        <Button variant="secondary" size="sm" onClick={() => list.refetch()}>
          {t("retry")}
        </Button>
      </div>
    );
  }
  if (list.data.data.length === 0) {
    return <EmptyState icon={<BellOff />} title={t("empty.title")} body={t("empty.body")} />;
  }
  return (
    <ul className="flex flex-col gap-0.5 p-1">
      {list.data.data.map((n) => (
        <NotificationItem key={n.uuid} notification={n} onSelect={onSelect} />
      ))}
    </ul>
  );
}

/**
 * The notifications dropdown: anchored beside the sidebar bell on desktop, a
 * sheet under the top bar on mobile. Non-modal; Escape or a click outside
 * closes it and focus returns to the bell. Built from Menu pieces until
 * anthm-design ships a dedicated container.
 */
export function NotificationsPanel({ anchor, onClose }: Readonly<{ anchor: HTMLElement | null; onClose: () => void }>) {
  const { t } = useTranslation("notifications");
  const navigate = useNavigate();
  const titleId = useId();
  const unread = useQuery(unreadCountOptions());
  const { markRead, markAllRead } = useNotificationActions();
  const panelRef = useRef<HTMLDialogElement>(null);
  const style = useBesideAnchor(anchor);

  useEffect(() => {
    panelRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!panelRef.current?.contains(target) && !anchor?.contains(target)) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
      anchor?.focus();
    };
  }, [anchor, onClose]);

  const select = (n: NotificationResponse) => {
    markRead(n);
    const href = notificationHref(n);
    if (href) {
      onClose();
      navigate(href);
    }
  };

  const hasUnread = (unread.data?.data.count ?? 0) > 0;

  return createPortal(
    // Non-modal (`open`, not showModal): the page stays usable, like the prototype's dropdown.
    <dialog
      open
      ref={panelRef}
      aria-labelledby={titleId}
      tabIndex={-1}
      style={style}
      className="z-[110] m-0 flex max-h-[min(40rem,calc(100dvh-1rem))] flex-col overflow-hidden rounded-xl border border-border-input bg-surface-elevated p-0 text-content-primary w-auto shadow-lg outline-none md:w-[26rem]"
    >
      <MenuHeader
        title={<span id={titleId}>{t("title")}</span>}
        action={
          hasUnread && (
            <Button variant="ghost" size="sm" onClick={markAllRead}>
              {t("markAllRead")}
            </Button>
          )
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <PanelBody onSelect={select} />
      </div>
      <MenuFooter
        start={
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onClose();
              navigate("/settings/notifications");
            }}
          >
            {t("settings")}
          </Button>
        }
      />
    </dialog>,
    document.body,
  );
}
